import { create } from 'zustand';
import type { UIMessage as Message } from '@ai-sdk/react';

interface ChatState {
  // Message history
  messages: Message[];

  // Modal visibility
  isModalVisible: boolean;

  // Session tracking
  sessionId: string;
  lastActiveTimestamp: number;

  // Signal for todo list to refresh after chat-created task
  taskRefreshSignal: number;

  // Navigate to todo tab after modal closes
  pendingTodoNavigation: boolean;

  // Actions
  openModal: () => void;
  closeModal: () => void;
  resetSession: () => void;
  setMessages: (messages: Message[]) => void;
  updateLastActive: () => void;
  incrementTaskRefreshSignal: () => void;
  requestTodoNavigation: () => void;
  clearTodoNavigation: () => void;
}

/**
 * Global chat store using Zustand
 * Manages shared chat state across all screens
 */
export const useChatStore = create<ChatState>((set) => ({
  // Initial state
  messages: [],
  isModalVisible: false,
  sessionId: generateSessionId(),
  lastActiveTimestamp: Date.now(),
  taskRefreshSignal: 0,
  pendingTodoNavigation: false,

  // Open the chat modal
  openModal: () => set({ isModalVisible: true }),

  // Close the chat modal
  closeModal: () => set({ isModalVisible: false }),

  // Reset session (clear messages and generate new ID)
  resetSession: () => set({
    messages: [],
    sessionId: generateSessionId(),
    lastActiveTimestamp: Date.now(),
  }),

  // Update message history
  setMessages: (messages) => set({ messages }),

  // Update last active timestamp
  updateLastActive: () => set({ lastActiveTimestamp: Date.now() }),

  // Signal todo list to refresh
  incrementTaskRefreshSignal: () => set((state) => ({
    taskRefreshSignal: state.taskRefreshSignal + 1,
  })),

  // Request navigation to todo tab after modal closes
  requestTodoNavigation: () => set({ pendingTodoNavigation: true }),
  clearTodoNavigation: () => set({ pendingTodoNavigation: false }),
}));

/**
 * Generate a unique session ID
 */
function generateSessionId(): string {
  return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}
